import { useEffect, useMemo, useState } from 'react';
import {
    CalculationEngineMonthlyValueOverrides,
    FarmerMarginCalculatorMonthlyModel,
    FarmerMarginCalculatorPricePercentLineModel,
} from '../../../../../Generated/Raven-Demeter';
import chartService from '../../../../Components/Charts/ChartService';
import RadioInput from '../../../../Components/Form/Inputs/RadioInput';
import ActionModal from '../../../../Components/Modals/ActionModal/ActionModal';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import { HideGroup, UpsertCalculationEngine } from '../../RiskDefinitions';
import styles from '../Margin.module.scss';
import MarginInputsTableCollapsibleField from './MarginInputsTableCollapsibleField';

export type AvailableFuturesProductOptions = {
    displayName: string;
    isChecked: boolean;
    lineValue1: FarmerMarginCalculatorPricePercentLineModel;
    lineValue2: FarmerMarginCalculatorPricePercentLineModel;
};

interface IMarginInputsTableFuturesBlendActionsProps {
    currentValue: FarmerMarginCalculatorMonthlyModel;
    availableFuturesProducts: AvailableFuturesProductOptions[];
    setAvailableFuturesProducts: (availableFuturesProducts: AvailableFuturesProductOptions[]) => void;
    hideGroup: HideGroup;
    setHideGroup: (hideGroup: HideGroup) => void;
    upsertCalculationEngine: UpsertCalculationEngine;
    setUpsertCalculationEngine: (upsertCalculationEngine: UpsertCalculationEngine) => void;
    runMonthlyValueOverrides: Array<CalculationEngineMonthlyValueOverrides> | null;
    setRunMonthlyValueOverrides: (runMonthlyValueOverrides: Array<CalculationEngineMonthlyValueOverrides> | null) => void;
}

const MarginInputsTableFuturesBlendActions: React.FC<IMarginInputsTableFuturesBlendActionsProps> = (props: IMarginInputsTableFuturesBlendActionsProps) => {
    // Application hooks.
    const [translations] = useLanguage();

    // Internal states.
    const [showEditProductsModal, setShowEditProductsModal] = useState(false);
    const [internalAvailableFuturesProducts, setInternalAvailableFuturesProducts] = useState<AvailableFuturesProductOptions[]>([]);
    const [internalFuturesProductsTextOverrides, setInternalFuturesProductsTextOverrides] = useState<string>();

    const futuresBlendPriceModalHeader = useMemo(() => translations.risk.margin.text.productsForFutures, [translations]);

    useEffect(() => {
        if (internalAvailableFuturesProducts.length > 0) {
            return;
        }

        const initialFuturesProductsTextOverrides = props.availableFuturesProducts
            .filter((x) => x.isChecked)
            .map((x) => x.lineValue1.percent.variableName)
            .join(',');

        setInternalFuturesProductsTextOverrides(initialFuturesProductsTextOverrides);
        setInternalAvailableFuturesProducts(props.availableFuturesProducts);
    }, [props.availableFuturesProducts]);

    return (
        <>
            <MarginInputsTableCollapsibleField
                fieldDisplayName={
                    <div className={styles.margin_futures_blend}>
                        <p>
                            {`${props.currentValue.futuresPrices.futuresBlendPrice.displayName} 
                                (${chartService.getCurrencyAndUnitOfMeasureText(
                                    props.currentValue.futuresPrices.futuresBlendPrice.unitOfMeasure,
                                    props.currentValue.futuresPrices.futuresBlendPrice.currency,
                                )}
                                )`}
                        </p>{' '}
                        {props.availableFuturesProducts.length > 0 && (
                            <button type="button" className={styles.margin_actions_futures_blend_modal} onClick={() => setShowEditProductsModal(true)}>
                                {translations.risk.margin.text.editProducts}
                            </button>
                        )}
                    </div>
                }
                hideGroup={props.hideGroup[props.currentValue.futuresPrices.futuresBlendPrice.variableName]}
                handleCollapse={() => {
                    const hideGroupKey = props.currentValue.futuresPrices.futuresBlendPrice.variableName;
                    const showGroupChildren = props.hideGroup[hideGroupKey] === false;

                    props.setHideGroup({
                        ...props.hideGroup,
                        [hideGroupKey]: showGroupChildren,
                    });
                }}
                hideActionButton={props.availableFuturesProducts.length === 0}
            />
            <ActionModal
                header={futuresBlendPriceModalHeader}
                showModal={showEditProductsModal}
                actionButtonName={translations.actions.save}
                handleAction={() => {
                    const newRunMonthlyValueOverrides = props.runMonthlyValueOverrides?.map((x) => {
                        internalAvailableFuturesProducts
                            .filter((y) => !y.isChecked)
                            .flatMap((y) => [y.lineValue1.percent.variableName, y.lineValue2.percent.variableName])
                            .forEach((y) => {
                                if (Object.keys(x.valueOverrides).includes(y)) {
                                    delete x.valueOverrides[y];
                                }
                            });

                        return x;
                    });
                    props.setRunMonthlyValueOverrides(newRunMonthlyValueOverrides!);
                    props.setAvailableFuturesProducts(internalAvailableFuturesProducts);
                    props.setUpsertCalculationEngine({
                        ...props.upsertCalculationEngine,
                        textOverrides: { ...props.upsertCalculationEngine.textOverrides, futuresProducts: internalFuturesProductsTextOverrides ?? '' },
                    });
                    setShowEditProductsModal(false);
                }}
                handleCancel={() => {
                    setInternalAvailableFuturesProducts(props.availableFuturesProducts);
                    setShowEditProductsModal(false);
                }}
                track={{ eventCategory: 'MarginCalculator', eventAction: 'ModalEditFuturesBlendPrice' }}
            >
                <p>
                    {props.availableFuturesProducts.map((x) => (
                        <div key={x.lineValue1.percent.variableName} className={styles.margin_actions_futures_blend_modal_item}>
                            <RadioInput
                                isChecked={
                                    internalAvailableFuturesProducts.find((y) => y.lineValue1.percent.variableName === x.lineValue1.percent.variableName)
                                        ?.isChecked!
                                }
                                handleIsChecked={(value) => {
                                    const currentFuturesProducts =
                                        internalFuturesProductsTextOverrides ?? props.upsertCalculationEngine.textOverrides.futuresProducts;
                                    const currentValue = props.availableFuturesProducts.find(
                                        (y) => x.lineValue1.percent.variableName === y.lineValue1.percent.variableName,
                                    );

                                    if (!currentValue) {
                                        return;
                                    }

                                    if (value) {
                                        setInternalFuturesProductsTextOverrides(
                                            `${currentValue.lineValue1.percent.variableName},${currentValue.lineValue2.percent.variableName}`,
                                        );
                                    } else if (!currentFuturesProducts) {
                                        setInternalFuturesProductsTextOverrides('');
                                    }

                                    setInternalAvailableFuturesProducts([
                                        ...props.availableFuturesProducts
                                            .filter((y) => currentValue.lineValue1.percent.variableName !== y.lineValue1.percent.variableName)
                                            .map((y) => ({ ...y, isChecked: false })),
                                        { ...currentValue, isChecked: value },
                                    ]);
                                }}
                                track={{
                                    eventCategory: 'MarginCalculator',
                                    eventAction: 'FuturesBlendChanged',
                                    eventValue: internalAvailableFuturesProducts.find(
                                        (y) => y.lineValue1.percent.variableName === x.lineValue1.percent.variableName,
                                    )?.isChecked,
                                }}
                            />
                            <p>{x.displayName.replace('Blend Percent', '')}</p>
                        </div>
                    ))}
                </p>
            </ActionModal>
        </>
    );
};

export default MarginInputsTableFuturesBlendActions;
